import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React from "react";
import { type TodoHint, TodoHintBody } from "./todo-hints";

/** The theme's tooltip has no max width; a hint is a few sentences. */
const HINT_TOOLTIP_SLOT_PROPS = { tooltip: { sx: { maxWidth: 360 } } };

/** Opens upward so it never covers the count badge beside it. */
const HintIcon = ({ hint }: { hint: TodoHint }) => (
	<Tooltip
		title={<TodoHintBody hint={hint} />}
		placement="top"
		slotProps={HINT_TOOLTIP_SLOT_PROPS}
	>
		<HelpOutlineIcon
			data-component="todo-hint-icon"
			sx={{ fontSize: 22, color: "text.secondary", cursor: "help" }}
		/>
	</Tooltip>
);

export const BaseTodoBox = ({
	className = "text-primary",
	number,
	title,
	description,
	to,
	readOnly = false,
	subRow,
	hint,
	dataComponent = "todo-box",
}: {
	className?: string;
	number?: React.ReactNode;
	title: React.ReactNode;
	description?: React.ReactNode;
	to?: string;
	readOnly?: boolean;
	subRow?: React.ReactNode;
	/** What this item holds, behind a help icon after the title. */
	hint?: TodoHint;
	/** Names this row for tests and screenshots, e.g. "todo-stale-conversations". */
	dataComponent?: string;
}) => {
	return (
		<Grid
			id={`BaseTodoBox ${title}`}
			size={{
				xs: 12,
				lg: 12,
			}}
		>
			<Paper className={className} sx={{ p: 2 }} data-component={dataComponent}>
				<Grid
					container
					sx={{
						alignItems: "center",
					}}
				>
					<Grid
						size={{
							xs: 12,
							lg: 8,
						}}
					>
						<Box>
							<Typography variant="h6" component="h6">
								<Grid
									container
									sx={{
										alignItems: "center",
										// The icon sits between the title and the
										// count badge, which AttentionBadge renders
										// as two siblings.
										'& > [data-component="attention-title-grid"]': {
											order: 0,
										},
										'& > [data-component="todo-hint-after-title"]':
											{
												order: 1,
											},
										'& > [data-component="attention-avatar-grid"]':
											{
												order: 2,
											},
									}}
								>
									{title}
									{hint ? (
										<Box
											data-component="todo-hint-after-title"
											sx={{ display: "flex", ml: 1 }}
										>
											<HintIcon hint={hint} />
										</Box>
									) : null}
								</Grid>
							</Typography>
						</Box>
					</Grid>
					<Grid
						size={{
							xs: 12,
							lg: 3,
						}}
					>
						{description}
					</Grid>
					{subRow ? <Grid size={12}>{subRow}</Grid> : null}
				</Grid>
			</Paper>
		</Grid>
	);
};
